import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './DropDownSelector.css';
import '../common.css';
import Icon from './Icon';
import React, { useEffect, useId } from 'react';
import { getKeyHandler } from '../structure/FlowForm';
import { checkmarkIcon, chevronDownIcon } from '../icons';
import { OrRuler } from '../structure/OrRuler';
import { Stack } from '../structure/Stack';
import { globalFocus } from '../helpers/dom';
const DropDownSelector = (({ className, focusId, noSelectionText = '', sidewaysBehaviour = 'off', onSelect, selections, value, prefix = '', focussable, disabled }) => {
    var _a, _b;
    const id = focusId || useId();
    const [isOpen, setIsOpen] = React.useState(false);
    const [userInput, setUserInput] = React.useState('');
    const selected = selections.find((item) => item.id == value);
    function focusByUserInput(input, add = false) {
        if (!input && !add) {
            setUserInput('');
            return;
        }
        else if (add)
            setUserInput((o) => o + input);
        else
            setUserInput(input);
        const userInputLc = (add ? userInput + input : input).toLowerCase();
        const target = selections.find((item) => item.searchTextLc.startsWith(userInputLc));
        if (target) {
            setTimeout(() => {
                var _a;
                (_a = document.querySelector(`#${CSS.escape(id)} [data-id="${target.id}"]`)) === null || _a === void 0 ? void 0 : _a.focus();
            }, isOpen ? 0 : 50);
        }
    }
    function close(newValue) {
        setIsOpen(false);
        focusByUserInput('');
        if (newValue && newValue.id != value)
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(newValue);
        globalFocus(id + '-selection');
    }
    function open(initKey = '') {
        setIsOpen(true);
        focusByUserInput(initKey);
    }
    const overrideHandlers = {
        'Escape': () => { close(); return true; },
        'Backspace': () => { focusByUserInput(userInput.slice(0, -1)); return true; },
    };
    const handleKeyDown = getKeyHandler({
        sidewaysBehaviour: sidewaysBehaviour,
        enterBehaviour: 'action',
        spaceBehaviour: 'action',
        onAction() {
            setIsOpen(o => !o);
            setUserInput('');
        },
        overrideHandlers,
        defaultHandler: (key) => {
            if (/^[a-zA-Z0-9-]$/.test(key)) {
                if (isOpen)
                    focusByUserInput(key, true);
                else
                    open(key);
                return true;
            }
            return false;
        },
    });
    const handleItemKeyDown = getKeyHandler({
        enterBehaviour: 'action',
        onAction: (target) => {
            close(selections.find((item) => { var _a; return item.id == ((_a = target === null || target === void 0 ? void 0 : target.dataset) === null || _a === void 0 ? void 0 : _a.id); }));
        },
        upDownBehaviour: (origin) => origin.dataset.last == 'true' ? 'up' : origin.dataset.first == 'true' ? 'down' : 'both',
        overrideHandlers,
        defaultHandler(key) {
            if (/^[a-zA-Z0-9-]$/.test(key)) {
                focusByUserInput(key, true);
                return true;
            }
            return false;
        },
    });
    useEffect(() => {
        function handleClickOutside(event) {
            const container = document.getElementById(id);
            if (container && !container.contains(event.target) && isOpen) {
                close();
            }
        }
        if (isOpen)
            document.addEventListener('mousedown', handleClickOutside);
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [isOpen, id]);
    return (_jsxs(_Fragment, { children: [_jsx("select", { id: id + '-select', className: `dropDownSelector touchOnly ${focussable != 'normal' && !disabled ? ' flowFocussable' : ''}${focussable == 'sideway' ? ' sidewayFocus' : ''} ${className !== null && className !== void 0 ? className : ''}`, tabIndex: 0, value: value || '', disabled: disabled, onKeyDown: handleKeyDown, onChange: (ev) => {
                    const value = ev.target.value;
                    const s = selections.find(s => s.id == value);
                    s && (onSelect === null || onSelect === void 0 ? void 0 : onSelect(s));
                }, children: selections.map((s, i) => _jsx("option", { value: s.id, children: s.text }, i)) }), _jsxs("div", { id: id, className: `dropDownSelector desktopOnly ${isOpen ? 'open' : 'close'} ${className !== null && className !== void 0 ? className : ''}`, onClick: () => {
                    var _a;
                    setIsOpen(o => !o);
                    (_a = document.getElementById(id + '-selection')) === null || _a === void 0 ? void 0 : _a.focus();
                }, children: [_jsxs("div", { className: 'top', children: [_jsx("div", { className: `selection flowFocussable${focussable == 'sideway' ? ' sidewayFocus' : ''}`, onKeyDown: handleKeyDown, id: id + '-selection', tabIndex: 0, children: (_a = selected === null || selected === void 0 ? void 0 : selected.selectionText) !== null && _a !== void 0 ? _a : ((selected === null || selected === void 0 ? void 0 : selected.text) ? (prefix + selected.text) : noSelectionText) }), _jsx(Stack, { children: _jsx(Icon, { icon: chevronDownIcon, size: 'medium', className: isOpen ? '' : 'show' }) })] }), isOpen && _jsxs("div", { className: `dropDownSelectorList`, children: [((_b = selections[0]) === null || _b === void 0 ? void 0 : _b.group) && _jsx(OrRuler, { text: selections[0].group, position: 'mid' }, 'group0'), selections.flatMap((item, i) => {
                                var _a;
                                const showGroup = (i + 2) <= selections.length && item.group != selections[i + 1].group && selections[i + 1].group;
                                return [
                                    _jsxs("div", { className: `item flowFocussable ${showGroup ? ' groupEnd' : ''} ${item.id == value ? 'selected' : ''}`, tabIndex: 0, "data-id": item.id, "data-first": i == 0 ? 'true' : 'false', "data-last": i == selections.length - 1 ? 'true' : 'false', onKeyDown: handleItemKeyDown, onClick: (ev) => { close(item); ev.stopPropagation(); }, children: [_jsx(Icon, { icon: checkmarkIcon, size: 'small' }), item.avatarUrl && _jsx("img", { src: item.avatarUrl, alt: item.text }), _jsx("span", { children: item.text })] }, item.id),
                                    ...(showGroup ? [_jsx(OrRuler, { text: selections[i + 1].group, position: 'mid' }, i + ((_a = selections[i + 1].group) !== null && _a !== void 0 ? _a : 'g'))] : [])
                                ];
                            })] })] })] }));
});
export default DropDownSelector;
